import { Card } from '@/components/ui/card';
import { Plane, Clock, Calendar, ExternalLink } from 'lucide-react';
import { FlightInfo } from './types';
import { cn } from '@/lib/utils';
// Importar as funções da nova biblioteca
import { formatFlightDate, formatFlightTime, getEnrichedFlightEventDateTime } from '@/lib/flight-datetime-utils';

interface SimpleFlightEventItemProps {
  flight: FlightInfo & { 
    _displayDates?: string;
    _displayTime?: string;
    _displayLocation?: string;
    flightId?: string;
  };
  eventType: 'departure' | 'arrival';
  className?: string;
  onViewDetails?: (flightId: string) => void;
}

export function SimpleFlightEventItem({ 
  flight, 
  eventType,
  className,
  onViewDetails
}: SimpleFlightEventItemProps) {
  const isDeparture = eventType === 'departure';
  const isArrival = eventType === 'arrival';
  
  // For arrival, use info from the last connection if available
  const hasConnections = flight.connections && flight.connections.length > 0;
  const lastConnection = hasConnections ? flight.connections[flight.connections.length - 1] : null;
  
  // Determine icon and title based on event type
  const icon = <Plane className={`h-5 w-5 text-blue-600`} />;
  const title = isDeparture ? 'Partida' : 'Chegada';
  
  // Get date and time using the new utility function
  let dateValue = '';
  let timeValue = '';
  
  // Check if we have enriched display properties
  const hasEnrichedProperties = '_displayDates' in flight || '_displayTime' in flight;
  
  if (hasEnrichedProperties) {
    // Use pre-calculated display values if available
    dateValue = flight._displayDates || '';
    timeValue = flight._displayTime || '';
  } else {
    // Calculate date and time values using the new utils
    const dateTime = isDeparture 
      ? flight.departureDateTime 
      : (isArrival && lastConnection) 
        ? lastConnection.arrivalDateTime 
        : flight.arrivalDateTime;
    
    // Use the new date formatting utility
    dateValue = formatFlightDate(dateTime);
    timeValue = formatFlightTime(dateTime);
  }
  
  // Get flight number
  const flightNumber = isDeparture 
    ? flight.flightNumber 
    : (isArrival && lastConnection) 
      ? lastConnection.flightNumber 
      : flight.flightNumber;
  
  const iconBgColor = 'bg-blue-100';
  const borderColor = 'border-l-blue-600';
  
  return (
    <Card className={cn(
      "border border-gray-200 border-l-4 shadow-sm overflow-hidden",
      borderColor,
      className
    )}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 ${iconBgColor} p-2 rounded-full`}>
            {icon}
          </div>
          
          <div className="flex-grow">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-gray-900">{title}</h3>
              <span className="text-sm text-gray-500">•</span>
              <span className="text-sm text-gray-600">{flightNumber}</span>
            </div>
            
            {/* Show date and time */}
            {(dateValue || timeValue) && (
              <div className="mt-2 flex items-center text-sm text-gray-600">
                {dateValue && (
                  <>
                    <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{dateValue}</span>
                  </>
                )}
                
                {dateValue && timeValue && <span className="mx-1">•</span>}
                
                {timeValue && (
                  <>
                    <Clock className="h-4 w-4 mx-1 text-gray-400" />
                    <span>{timeValue}</span>
                  </>
                )}
              </div>
            )}
            
            {/* View Details Button */}
            {onViewDetails && (
              <div className="mt-3 pt-2 border-t border-gray-100">
                <button
                  onClick={() => onViewDetails('flightId' in flight && flight.flightId ? flight.flightId : flight.id)}
                  className="text-sm text-voah-teal hover:text-voah-teal-dark flex items-center transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Ver detalhes completos
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
} 